import { useCallback } from 'react';
import type MapView from '@arcgis/core/views/MapView';
import { useEsriMapContext } from '../EsriMapContext';

/**
 * Disables map interactions on an ArcGIS MapView: removes popup zoom actions,
 * clears UI components (zoom etc.), and stops propagation for mouse-wheel,
 * double-click, drag, and key-down zoom/pan.
 * Shared so both the EsriMap class and useMapModes hook can apply the same behavior.
 */
export async function disableMapInteractionsForView(
	view: MapView | null,
): Promise<void> {
	if (!view) {
		return;
	}
	await view.when();
	if (view?.popup) {
		view.popup.actions = [];
	}
	if (view?.ui) {
		view.ui.components = [];
	}

	const stopEvtPropagation = (event: { stopPropagation: () => void }) => {
		event.stopPropagation();
	};

	view?.on('mouse-wheel', stopEvtPropagation as (event: unknown) => void);
	view?.on('double-click', stopEvtPropagation as (event: unknown) => void);
	view?.on(
		'double-click',
		['Control'],
		stopEvtPropagation as (event: unknown) => void,
	);
	view?.on('drag', stopEvtPropagation as (event: unknown) => void);
	view?.on('drag', ['Shift'], stopEvtPropagation as (event: unknown) => void);
	view?.on(
		'drag',
		['Shift', 'Control'],
		stopEvtPropagation as (event: unknown) => void,
	);
	view?.on('key-down', event => {
		const prohibitedKeys = [
			'+',
			'-',
			'Shift',
			'_',
			'=',
			'ArrowUp',
			'ArrowDown',
			'ArrowRight',
			'ArrowLeft',
		];
		if (prohibitedKeys.indexOf(event.key) !== -1) {
			event.stopPropagation();
		}
	});
}

/**
 * Hook for map interaction modes (e.g. disabling zoom/pan). Uses the view from
 * EsriMapContext and exposes disableMapInteractions from index-esri.ts behavior.
 */
export const useMapModes = () => {
	const { view } = useEsriMapContext();

	const disableMapInteractions = useCallback(async (): Promise<void> => {
		await disableMapInteractionsForView(view);
	}, [view]);

	return {
		disableMapInteractions,
	};
};
